import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";

// Схемы для турниров
/**
 * @deprecated Используйте goodLuckCoreEntitiesTournamentSchema из generated-kubb/schemas
 */
export const TournamentSchema = z.lazy(() =>
  z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ""),
  type: z.string(),
  bracketType: z.string(),
  prizePool: z.preprocess((v) => v ?? 0, z.number().min(0)),
  createdById: z.string(),
  createdByName: z.string(),
  startDate: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ""),
  endDate: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ""),
  maxPlayers: z.preprocess((v) => v ?? 0, z.number().int().min(0)),
  minTeamSize: z.number().int().optional(),
  maxTeamSize: z.number().int().optional(),
  status: z.string(), // Accept any status string from backend
  createdAt: z.string(),
  registeredPlayersCount: z.number().int().min(0),
  registeredUserIds: z.array(z.string()).optional(),

  // Additional fields
  format: z.string(),
  game: z.string(),
  currency: z.string(),
  location: z.string(),
  currentTeams: z.number().int().min(0),
  maxTeams: z.preprocess((v) => v ?? 0, z.number().int().min(0)),
  image: z.string().optional(),

  organizer: z
    .object({
      id: z.string(),
      name: z.string(),
      avatar: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      currentUserRole: z.string().nullable().optional(),
    })
    .optional(),

  capabilities: z
    .object({
      canRegister: z.boolean(),
      canCancelRegistration: z.boolean(),
      canApproveRegistrations: z.boolean(),
      canAdjustPoints: z.boolean(),
      canBlacklist: z.boolean(),
      canUnblacklist: z.boolean(),
      canGenerateBracket: z.boolean(),
      canFreezeBracket: z.boolean(),
      canUnfreezeBracket: z.boolean(),
      canReshuffleBracket: z.boolean(),
      canStartDraft: z.boolean(),
      canEditTournament: z.boolean(),
      canDeleteTournament: z.boolean(),
      canManageTournament: z.boolean().optional(),
      disabledReasons: z.record(z.string(), z.string()).nullable().optional(),
    })
    .nullable()
    .optional(),

  draftStartTime: z.string().nullable().optional(),
  draftStartAt: z.string().nullable().optional(),
  draftStartedAt: z.string().nullable().optional(),
  draftCaptainBudget: z.number().int().nullable().optional(),
  draftRankCosts: z.record(z.string(), z.number().int()).nullable().optional(),
  // KAN-89: гранулярная модель — список записей {tier, division?, lpFrom?, value}.
  draftRankCostEntries: z
    .array(
      z.object({
        tier: z.string(),
        division: z.string().nullable().optional(),
        lpFrom: z.number().int().nullable().optional(),
        value: z.number().int(),
      }),
    )
    .optional()
    .nullable(),
  draftPickTimeoutSeconds: z.number().int().nullable().optional(),
  draftTimeoutAction: z.string().nullable().optional(),
  draftNoPickAction: z.string().nullable().optional(),
  registrationApprovalMode: z.string().nullable().optional(),

  // Schedule
  scheduleDays: z
    .array(
      z.object({
        date: z.string(),
        time: z.string(),
        stageType: z.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
}));

export const TournamentTeamSchema = z.lazy(() =>
  z.object({
  id: z.number().int().positive({ error: () => translateUi("litTournamentSchemas.idKomandyDolzhenBytPolozhitelnymChislom") }),
  name: z.string().min(1, { error: () => translateUi("litTournamentSchemas.nazvanieKomandyObyazatelno") }),
  logo: z.string().url({ error: () => translateUi("litTournamentSchemas.nekorrektnyyUrlLogotipa") }).optional(),
  seed: z.number().int().min(1, { error: () => translateUi("litTournamentSchemas.seedDolzhenBytPolozhitelnymChislom") }),
  players: z.array(z.string().min(1, { error: () => translateUi("litTournamentSchemas.imyaIgrokaObyazatelno") })),
}));

export const TournamentScheduleSchema = z.lazy(() =>
  z.object({
  date: z.string().datetime({ error: () => translateUi("litTournamentSchemas.nekorrektnayaData") }),
  time: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { error: () => translateUi("litTournamentSchemas.nekorrektnoeVremya") }),
  match: z.string().min(1, { error: () => translateUi("litTournamentSchemas.nazvanieMatchaObyazatelno") }),
  teams: z.array(z.string().min(1, { error: () => translateUi("litTournamentSchemas.nazvanieKomandyObyazatelno") })),
}));

export const CreateTournamentRequestSchema = z.lazy(() =>
  z.object({
  organizationId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdOrganizatsii") }),
  name: z
    .string()
    .min(3, { error: () => translateUi("litTournamentSchemas.nazvanieTurniraDolzhnoSoderzhatMinimum3") })
    .max(100, { error: () => translateUi("litTournamentSchemas.nazvanieTurniraNeDolzhnoPrevyshat100") })
    .trim(),
  description: z
    .string()
    .max(1000, { error: () => translateUi("litTournamentSchemas.opisanieTurniraNeDolzhnoPrevyshat1000") })
    .trim()
    .optional(),
  // KAN-346: game/currency/location здесь НЕ объявляем — таких полей нет в
  // бэкендовом TournamentCreateRequest и никогда не было (проверено по OpenAPI).
  // `game` при этом стоял обязательным: как только визард перестал слать его в
  // пустоту, клиентская валидация начала рубить создание турнира 400-й ошибкой
  // ZOD_VALIDATION_ERROR. Схема обязана описывать реальный контракт, иначе она
  // не защищает, а ломает.
  region: z
    .enum(
      [
        "BR1",
        "EUN1",
        "EUW1",
        "LA1",
        "LA2",
        "NA1",
        "OC1",
        "RU",
        "TR1",
        "ME1",
        "JP1",
        "KR",
        "SEA",
        "TW2",
        "VN2",
      ] as const,
      { error: () => translateUi("litTournamentSchemas.nekorrektnyyRegion") },
    )
    .optional(),
  type: z.enum(["Classic", "Draft", "SoloOneVsOne", "Arena"]),
  bracketType: z.enum([
    "SingleElimination",
    "DoubleElimination",
    "RoundRobin",
    "SwissSystem",
    "Swiss",
  ]),
  maxPlayers: z.number().int().min(2, { error: () => translateUi("litTournamentSchemas.minimum2Uchastnika") }),
  restrictRank: z.boolean().optional(),
  minRankTier: z
    .enum(
      [
        "IRON",
        "BRONZE",
        "SILVER",
        "GOLD",
        "PLATINUM",
        "EMERALD",
        "DIAMOND",
        "MASTER",
        "GRANDMASTER",
        "CHALLENGER",
      ] as const,
      { error: () => translateUi("litTournamentSchemas.nekorrektnyyTier") },
    )
    .optional(),
  minRankDivision: z.enum(["I", "II", "III", "IV"]).optional(),
  prizePool: z.number().min(0, { error: () => translateUi("litTournamentSchemas.prizovoyFondNeMozhetBytOtritsatelnym") }),
  prizeDescription: z.string().max(2000).optional().nullable(),
  prizeDistribution: z
    .array(
      z.object({
        placeFrom: z.number().int(),
        placeTo: z.number().int(),
        prizeType: z.enum(["Money", "InGame", "Physical"]),
        sharePercent: z.number().optional().nullable(),
        quantity: z.number().int().optional().nullable(),
        description: z.string().max(500).optional().nullable(),
      }),
    )
    .optional()
    .nullable(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minTeamSize: z.number().int().optional(),
  maxTeamSize: z.number().int().optional(),
  draftBudget: z.number().int().optional(),
  draftCaptainBudget: z.number().int().optional().nullable(),
  draftRankCosts: z.record(z.string(), z.number().int()).optional().nullable(),
  // KAN-89: гранулярная модель — список записей {tier, division?, lpFrom?, value}.
  draftRankCostEntries: z
    .array(
      z.object({
        tier: z.string(),
        division: z.string().nullable().optional(),
        lpFrom: z.number().int().nullable().optional(),
        value: z.number().int(),
      }),
    )
    .optional()
    .nullable(),
  draftPickTimeoutSeconds: z.number().int().optional().nullable(),
  draftTimeoutAction: z.string().optional().nullable(),
  draftNoPickAction: z.string().optional().nullable(),
  draftType: z.enum(["Classic", "Snake"]).optional(),
  draftOrderMode: z.enum(["Auto", "Manual"]).optional(),
  maxTeamsInDraft: z.number().int().optional(),
  draftStartTime: z.string().optional().nullable(),
  draftStartAt: z.string().optional().nullable(),
  registrationApprovalMode: z.enum(["Auto", "Manual"]).optional(),
  image: z.string().optional(),
  // Discord Integration Settings
  enableDiscordIntegration: z.boolean().default(false),
  discordGuildId: z.string().optional(),
  discordCategoryId: z.string().optional(),
  discordNewsChannelId: z.string().optional(),
  discordNewsChannelPublicReadOnly: z.boolean().default(false),
  discordDraftRoleId: z.string().optional(),
  discordCaptainRoleId: z.string().optional(),
  discordInMatchRoleId: z.string().optional(),
  discordWinnerRoleId: z.string().optional(),
  discordStreamerRoleId: z.string().optional(),
  discordModeratorRoleId: z.string().optional(),
  allowCoachesInChannels: z.boolean().default(true),
  allowSubstitutesInChannels: z.boolean().default(true),
  allowStreamersInChannels: z.boolean().default(false),
  allowWebModeratorsInChannels: z.boolean().default(true),
  autoSendMatchStart: z.boolean().default(true),
  autoSendBracketProgress: z.boolean().default(true),
  autoSendRegistrationClose: z.boolean().default(true),
  // Поля ниже раньше отсутствовали в схеме → Zod .parse() их ВЫРЕЗАЛ, и бэк применял
  // дефолты (Arena → PreMade, requireRiotAccount → true, bestOf → 1, чек-ин/waitlist/
  // тайминги реги игнорировались). Зеркалят backend TournamentCreateRequest.
  enableThirdPlaceMatch: z.boolean().optional(),
  enableGrandFinal: z.boolean().optional(),
  startTimeTentative: z.boolean().optional(),
  scheduleDays: z.array(z.any()).optional(),
  bestOf: z.number().int().optional(),
  manualWinnerMode: z.string().optional(),
  requireRiotAccount: z.boolean().optional(),
  // KAN-928: подсказки по составу при привязке реплеев.
  allowReplayRosterMatching: z.boolean().optional(),
  arenaGamesCount: z.number().int().optional(),
  arenaScoringJson: z.string().optional().nullable(),
  arenaTeamMode: z.string().optional(),
  arenaLobbyCount: z.number().int().optional(),
  arenaTasksDocUrl: z.string().optional(),
  matchStartDeadlineEnabled: z.boolean().optional(),
  registrationOpenBeforeDays: z.number().int().optional(),
  registrationCloseBeforeMinutes: z.number().int().optional(),
  enableCheckIn: z.boolean().optional(),
  checkInWindowMinutes: z.number().int().optional(),
  waitlistEnabled: z.boolean().optional(),
  waitlistMode: z.string().optional(),
  waitlistPriorityRule: z.string().optional(),
  customRegistrationFields: z.array(z.any()).optional(),
}));

export const UpdateTournamentRequestSchema = z.lazy(() =>
  z.object({
  // Обязательные поля для бэкенда
  type: z.string().optional(),
  bracketType: z.string().optional(),
  organizationId: z.string().optional(),
  maxPlayers: z.number().optional(),
  minTeamSize: z.number().optional(),
  maxTeamSize: z.number().optional(),

  name: z
    .string()
    .min(3, { error: () => translateUi("litTournamentSchemas.nazvanieTurniraDolzhnoSoderzhatMinimum3") })
    .max(100, { error: () => translateUi("litTournamentSchemas.nazvanieTurniraNeDolzhnoPrevyshat100") })
    .trim()
    .optional(),
  description: z
    .string()
    .max(1000, { error: () => translateUi("litTournamentSchemas.opisanieTurniraNeDolzhnoPrevyshat1000") })
    .trim()
    .optional(),
  image: z.string().optional(),
  status: z.enum(["draft", "active", "completed", "cancelled"]).optional(),
  prizePool: z
    .number()
    .min(0, { error: () => translateUi("litTournamentSchemas.prizovoyFondNeMozhetBytOtritsatelnym") })
    .optional(),
  prizeDescription: z.string().max(2000).optional().nullable(),
  prizeDistribution: z
    .array(
      z.object({
        placeFrom: z.number().int(),
        placeTo: z.number().int(),
        prizeType: z.enum(["Money", "InGame", "Physical"]),
        sharePercent: z.number().optional().nullable(),
        quantity: z.number().int().optional().nullable(),
        description: z.string().max(500).optional().nullable(),
      }),
    )
    .optional()
    .nullable(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  // KAN-665: регламент турнира свободным текстом (пустая строка = очистить).
  rules: z
    .string()
    .max(20000, { error: () => translateUi("litTournamentSchemas.reglamentTurniraNeDolzhnoPrevyshat20000") })
    .optional(),

  // Discord Integration Settings
  enableDiscordIntegration: z.boolean().optional(),
  discordGuildId: z.string().optional(),
  discordCategoryId: z.string().optional(),
  discordNewsChannelId: z.string().optional(),
  discordNewsChannelPublicReadOnly: z.boolean().optional(),
  discordDraftRoleId: z.string().optional(),
  discordCaptainRoleId: z.string().optional(),
  discordInMatchRoleId: z.string().optional(),
  discordWinnerRoleId: z.string().optional(),
  discordStreamerRoleId: z.string().optional(),
  discordModeratorRoleId: z.string().optional(),
  allowCoachesInChannels: z.boolean().optional(),
  allowSubstitutesInChannels: z.boolean().optional(),
  allowStreamersInChannels: z.boolean().optional(),
  allowWebModeratorsInChannels: z.boolean().optional(),
  autoSendMatchStart: z.boolean().optional(),
  autoSendBracketProgress: z.boolean().optional(),
  autoSendRegistrationClose: z.boolean().optional(),
  discordBracketChannelId: z.string().optional(),
  broadcastBracketToDiscord: z.boolean().optional(),

  // Трансляции Twitch
  showPlayerStreamers: z.boolean().optional(),
  showOfficialStream: z.boolean().optional(),

  // Schedule
  scheduleDays: z
    .array(
      z.object({
        date: z.string(),
        time: z.string(),
        stageType: z.string().nullable().optional(),
      }),
    )
    .optional(),

  draftStartTime: z.string().datetime().optional(),
  draftStartAt: z.string().datetime().optional(),
  draftCaptainBudget: z.number().int().optional().nullable(),
  draftRankCosts: z.record(z.string(), z.number().int()).optional().nullable(),
  // KAN-89: гранулярная модель — список записей {tier, division?, lpFrom?, value}.
  draftRankCostEntries: z
    .array(
      z.object({
        tier: z.string(),
        division: z.string().nullable().optional(),
        lpFrom: z.number().int().nullable().optional(),
        value: z.number().int(),
      }),
    )
    .optional()
    .nullable(),
  draftPickTimeoutSeconds: z.number().int().optional().nullable(),
  draftTimeoutAction: z.string().optional().nullable(),
  draftNoPickAction: z.string().optional().nullable(),
  registrationApprovalMode: z.enum(["Auto", "Manual"]).optional(),
  manualWinnerMode: z.string().optional(),
  matchStartDeadlineEnabled: z.boolean().optional(),
  useDynamicSchedule: z.boolean().optional(),
  roundBufferMinutes: z.number().int().min(0).max(120).optional().nullable(),
  pausesAllowedPerMatch: z.number().int().min(0).optional().nullable(),
  pauseDurationMinutes: z.number().int().min(0).optional().nullable(),
  registrationOpenBeforeDays: z.number().int().min(0).max(90).optional(),
  registrationCloseBeforeMinutes: z.number().int().min(0).max(10080).optional(),
  enableCheckIn: z.boolean().optional(),
  checkInWindowMinutes: z.number().int().min(5).max(120).optional().nullable(),
  customRegistrationFields: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        type: z.enum(["Text", "Textarea", "Select"]),
        required: z.boolean(),
        options: z.array(z.string()).nullable().optional(),
      }),
    )
    .optional(),
  // Раньше отсутствовали → редактирование Arena/Riot/waitlist/bestOf не сохранялось
  // (Zod .parse() вырезал). Зеркалят backend TournamentCreateRequest (он же DTO update).
  enableThirdPlaceMatch: z.boolean().optional(),
  enableGrandFinal: z.boolean().optional(),
  startTimeTentative: z.boolean().optional(),
  bestOf: z.number().int().optional(),
  requireRiotAccount: z.boolean().optional(),
  // KAN-928: подсказки по составу при привязке реплеев.
  allowReplayRosterMatching: z.boolean().optional(),
  arenaGamesCount: z.number().int().optional(),
  arenaScoringJson: z.string().optional().nullable(),
  arenaTeamMode: z.string().optional(),
  arenaLobbyCount: z.number().int().optional(),
  arenaTasksDocUrl: z.string().optional(),
  waitlistEnabled: z.boolean().optional(),
  waitlistMode: z.string().optional(),
  waitlistPriorityRule: z.string().optional(),
}));

export const TournamentRegistrationSchema = z.lazy(() =>
  z.object({
  tournamentId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdTurnira") }),
  userId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdPolzovatelya") }),
  teamId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdKomandy") }).nullable().optional(),
  registeredAt: z.string().nullable().optional(),
}));

/**
 * Схема соло-регистрации.
 *
 * Роли перечислены значениями бэкенда (`Top/Jungle/Mid/Adc/Support`). Раньше здесь
 * был рукописный список в нижнем регистре плюс несуществующий `fill`, из-за чего
 * выведенный тип противоречил реальному запросу; заметили это только когда убрали
 * `as unknown as` в use-tournament-registration. Отсутствие роли выражается её
 * отсутствием в payload — «любой роли» как значения на бэкенде нет.
 */
export const SoloPlayerRegistrationSchema = z.lazy(() =>
  z.object({
  tournamentId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdTurnira") }),
  riotAccountId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdAkkauntaRiot") }),
  mainRole: z.enum(["Top", "Jungle", "Mid", "Adc", "Support"]).optional(),
  secondaryRole: z.enum(["Top", "Jungle", "Mid", "Adc", "Support"]).optional(),
  desiredRole: z.enum(["Participant", "Captain"], {
    error: () => translateUi("litTournamentSchemas.nekorrektnayaZhelaemayaRol"),
  }),
  /** Заметка игрока о себе для капитанов (до 200 символов). */
  playerNote: z.string().max(200).optional(),
  customFields: z.record(z.string(), z.string()).nullable().optional(),
}));

export const TeamRegistrationSchema = z.lazy(() =>
  z.object({
  tournamentId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdTurnira") }),
  teamId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdKomandy") }).optional(),
  riotAccountId: z.string().uuid({ error: () => translateUi("litTournamentSchemas.nekorrektnyyIdAkkauntaRiot") }),
  customFields: z.record(z.string(), z.string()).nullable().optional(),
}));

export const TournamentParamsSchema = z.lazy(() =>
  z.object({
  status: z.string().optional(),
  game: z.string().optional(),
  page: z
    .number()
    .int()
    .min(1, { error: () => translateUi("litTournamentSchemas.stranitsaDolzhnaBytPolozhitelnymChislom") })
    .optional(),
  limit: z
    .number()
    .int()
    .min(1, { error: () => translateUi("litTournamentSchemas.limitDolzhenBytPolozhitelnymChislom") })
    .max(100, { error: () => translateUi("litTournamentSchemas.limitNeDolzhenPrevyshat100") })
    .optional(),
}));

// Типы для экспорта
/**
 * @deprecated Используйте Tournament из shared/api/tournament (z.infer<typeof goodLuckCoreEntitiesTournamentSchema>)
 */
export type TeamRegistrationDTO = z.infer<typeof TeamRegistrationSchema>;
